import React, {useState, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import dayjs from 'dayjs';
import Coffee from '../../assets/icons/Clipboard.svg';
import Package from '../../assets/icons/Package.svg';
import Users from '../../assets/icons/Users.svg';
import Tool from '../../assets/icons/Tool.svg';
import Calendar from '../../assets/icons/Calendar.svg';
import Book from '../../assets/icons/Book.svg';
import ExcelExportIcon from '../../assets/icons/Excel_Export.svg';

import apiClient from '../../context/apiClient';
import {useAuth} from '../../context/AuthContext';
import {useNavigate} from 'react-router-dom';
import useRules from '../../hooks/useRules';

const eventTypes = [
    'ADEC', 'GROS_DOLLN', 'TOUREN', 'ASCE', 'FINNLAND', 'SEEFELD',
    'SAALBACH', 'ADE', 'NICHT_EVENT'
];

const eventTypeLabels = {
    ADEC: 'ADEC',
    GROS_DOLLN: 'Groß Dölln',
    TOUREN: 'Touren',
    ASCE: 'ASCE',
    FINNLAND: 'Finnland',
    SEEFELD: 'Seefeld',
    SAALBACH: 'Saalbach',
    ADE: 'ADE',
    NICHT_EVENT: 'Nicht-Eventeinsätze'
};

// remove hardcoded paketPreise and excludedNames (now loaded from backend)

const Leistungspakete = () => {
    const {user} = useAuth();
    const navigate = useNavigate();
    const { paketPreise, excludedSet, loadingRules } = useRules();
    const [searchParams] = useSearchParams();

    const [from, setFrom] = useState(() =>
        searchParams.get('from') || dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
    );
    const [to, setTo] = useState(() =>
        searchParams.get('to') || dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    );

    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [stundenKontingente, setStundenKontingente] = useState({});
    const [nichtEventStundenkontingente, setNichtEventStundenkontingente] = useState([]);


    if (!user) {
        navigate('/login');
    }

    const fetchAllEvents = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/api/events/getEventsFromTo', {
                params: {
                    from: dayjs(from).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
                    to: dayjs(to).endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
                },
                withCredentials: true,
            });
            setEvents(response.data);
            console.log('Events: ', response.data);
        } catch (err) {
            console.error('Fehler beim Laden aller Events:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchNichtEventStundenkontingente = async () => {
        try {
            const response = await apiClient.get('/api/employee/getStundenkontingenteByTimeAndCategory', {
                params: {
                    startTime: `${from}T00:00:00`,
                    endTime: `${to}T23:59:59`,
                    category: 'NICHT_EVENT',
                },
                withCredentials: true,
            });

            const totalHours = response.data || 0;

            // Calculate FSK (10 hours) and FSKHT (5 hours) packages
            const fskCount = Math.floor(totalHours / 10);
            const remainingHours = totalHours % 10;
            const fskhtCount = Math.ceil(remainingHours / 5);

            // Add these packages to your instruktoren counter
            const nichtEventPakete = {};
            if (fskCount > 0) nichtEventPakete['FSK'] = fskCount;
            if (fskhtCount > 0) nichtEventPakete['FSKHT'] = fskhtCount;

            setNichtEventStundenkontingente(nichtEventPakete);

            console.log('Nicht-Event calculation:', {
                totalHours,
                fskCount,
                fskhtCount,
                packages: nichtEventPakete
            });

        } catch (err) {
            console.error('Fehler beim Laden der Nicht-Event-Stundenkontingente:', err);
        }
    }

    const fetchStundenkontingente = async () => {
        const kontingenteMap = {};

        await Promise.all(eventTypes.map(async (type) => {
            try {
                const res = await apiClient.get('/api/employee/getStundenkontingenteVorNachFromTo', {
                    params: {
                        startTime: `${from}T00:00:00`,
                        endTime: `${to}T23:59:59`,
                        type,
                    },
                    withCredentials: true,
                });
                const resultList = res.data || [];
                const full = resultList[2];
                const half = resultList[3];
                kontingenteMap[type] = full * paketPreise['FZ6AVorNach'] + half * paketPreise['FZ6AHTVorNach'];

                console.log(`Stundenkontingente für ${type}:`, kontingenteMap[type]);
            } catch (err) {
                console.error(`Fehler beim Laden der Stundenkontingente für ${type}:`, err);
                kontingenteMap[type] = 0;
            }
        }));
        setStundenKontingente(kontingenteMap);
    };

    useEffect(() => {
        fetchAllEvents();
        fetchStundenkontingente();
        fetchNichtEventStundenkontingente();
    }, []);

    const groupedEvents = events.reduce((acc, event) => {
        const key = event.eventType;
        if (!acc[key]) acc[key] = [];
        acc[key].push(event);
        return acc;
    }, {});

    const sumPaketeByFilter = (eventsForType, filterFn) => {
        let sum = 0;
        const packageCounts = {};

        eventsForType.forEach(event => {
            const allRoles = [...(event.employeeList || []), ...(event.leader ? [event.leader] : [])];
            allRoles.forEach(person => {
                const name = `${person.firstName}, ${person.lastName}`;
                if (excludedSet.has(name)) return;

                const pakete = event.leistungspakete?.[person.id] || [];
                pakete.forEach(p => {
                    if (filterFn(p)) {
                        // Count packages
                        packageCounts[p] = (packageCounts[p] || 0) + 1;
                        // Calculate cost
                        sum += paketPreise[p] || 0;
                    }
                });
            });
        });

        return sum;
        // return {sum, packageCounts};
    };

    // const getOverallPackageCounts = () => {
    //     const overallCounts = {};
    //
    //     eventTypes.forEach(type => {
    //         if (type === 'NICHT_EVENT') {
    //             // Add Nicht-Event packages
    //             Object.entries(nichtEventStundenkontingente).forEach(([paket, count]) => {
    //                 overallCounts[paket] = (overallCounts[paket] || 0) + count;
    //             });
    //         } else {
    //             const eventsForType = groupedEvents[type] || [];
    //
    //             // Get package counts for each filter
    //             const { packageCounts: fsCounts } = sumPaketeByFilter(eventsForType, p => p.startsWith('FS'));
    //             const { packageCounts: fzCounts } = sumPaketeByFilter(eventsForType, p => p.startsWith('FZ'));
    //             const { packageCounts: tbCounts } = sumPaketeByFilter(eventsForType, p => p.startsWith('TB'));
    //
    //             // Merge all counts
    //             [fsCounts, fzCounts, tbCounts].forEach(counts => {
    //                 Object.entries(counts).forEach(([paket, count]) => {
    //                     overallCounts[paket] = (overallCounts[paket] || 0) + count;
    //                 });
    //             });
    //         }
    //     });
    //
    //     console.log('Overall package counts:', overallCounts);
    //     return overallCounts;
    // };
    //
    // const overallPackageCounts = getOverallPackageCounts();

    const handleExport = async () => {
        try {
            setIsDownloading(true);
            const response = await apiClient.get(
                `/api/events/getLPExcelExportFromTo?from=${from}T00:00:00&to=${to}T23:59:59`,
                {responseType: 'blob', withCredentials: true}
            );

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Leistungspakete_Alle_${from}_bis_${to}.xlsx`;
            a.click();
        } catch (err) {
            console.error('Download fehlgeschlagen:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSelect = (type) => {
        navigate(`/leistungspakete/${type}?from=${from}&to=${to}`);
    };

    let dynamicTotalSumme = 0;

    const cardElements = eventTypes.map(type => {
        const eventsForType = groupedEvents[type] || [];
        const vorNachSum = stundenKontingente[type] || 0;
        const fsSum = sumPaketeByFilter(eventsForType, p => p.startsWith('FS'));
        const fzSum = sumPaketeByFilter(eventsForType, p => p.startsWith('FZ')) + vorNachSum;
        const tbSum = sumPaketeByFilter(eventsForType, p => p.startsWith('TB'));
        const reiseSum = sumPaketeByFilter(eventsForType, p => p.toLowerCase().includes('reise'));
        const total = fsSum + tbSum + fzSum;
        const eventUmsetzung = total - reiseSum - vorNachSum;

        dynamicTotalSumme += total;

        if (type === 'NICHT_EVENT') {
            // Calculate total value for Nicht-Event packages
            const nichtEventTotal = Object.entries(nichtEventStundenkontingente).reduce((sum, [paket, count]) =>
                sum + (count * (paketPreise[paket] || 0)), 0
            );

            dynamicTotalSumme += nichtEventTotal;

            return (
                <div
                    key={type}
                    onClick={() => handleSelect(type)}
                    className="cursor-pointer bg-white shadow-sm rounded-xl p-10 hover:shadow-md transition flex flex-col justify-between"
                >
                    <div className="flex flex-col items-center mb-4">
                        <span
                            className="text-2xl font-bold text-gray-900">{nichtEventTotal.toLocaleString('de-DE')}€</span>
                        <span className="text-gray-500 text-sm font-medium">{eventTypeLabels[type]}</span>
                    </div>
                    <div className="flex justify-center gap-6 text-sm text-gray-800 font-medium">
                        {Object.entries(nichtEventStundenkontingente)
                            .filter(([, count]) => count > 0)
                            .map(([paket, count]) => (
                                <div key={paket} className="flex flex-col items-center gap-1">
                                    <span className="text-lg font-bold">{count}</span>
                                    <span className="text-xs">{paket}</span>
                                    <span
                                        className="text-xs">{(count * (paketPreise[paket] || 0)).toLocaleString('de-DE')}€</span>
                                </div>
                            ))}
                    </div>
                </div>
            );
        }

        return (
            <div
                key={type}
                onClick={() => handleSelect(type)}
                className="cursor-pointer bg-white shadow-sm rounded-xl p-10 hover:shadow-md transition flex flex-col justify-between"
            >
                <div className="flex flex-col items-center mb-4">
                    <span className="text-2xl font-bold text-gray-900">{total.toLocaleString('de-DE')}€</span>
                    <span className="text-gray-500 text-sm font-medium">{eventTypeLabels[type]}</span>
                </div>
                <div className="grid grid-cols-3 gap-x-8 gap-y-2 text-sm text-gray-800 font-medium mb-2">
                    <div className="flex items-center gap-2">
                        <img src={Calendar} className="w-5 h-5" alt="Reise" title="Eventumsetzung"/>
                        <span>{eventUmsetzung.toLocaleString('de-DE')}€</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <img src={Book} className="w-5 h-5" alt="Vor-/Nach" title="Vor-/Nachbereitung"/>
                        <span>{vorNachSum.toLocaleString('de-DE')}€</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <img src={Package} className="w-5 h-5" alt="—" title="Reisepakete"/>
                        <span>{reiseSum.toLocaleString('de-DE')}€</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <img src={Users} className="w-5 h-5" alt="FS" title="Instruktoren"/>
                        <span>{fsSum.toLocaleString('de-DE')}€</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <img src={Tool} className="w-5 h-5" alt="FZ" title="Mechanikeren"/>
                        <span>{fzSum.toLocaleString('de-DE')}€</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <img src={Coffee} className="w-5 h-5" alt="TB" title="Guestmanager"/>
                        <span>{tbSum.toLocaleString('de-DE')}€</span>
                    </div>
                </div>
            </div>
        );
    });

    return (
        <main className="flex flex-col flex-1 h-full overflow-hidden">
            <div className="relative flex items-center justify-between flex-wrap gap-4 mb-6">
                <div></div>
                {/* Left: Export Button */}
                {/*<button*/}
                {/*    onClick={handleExport}*/}
                {/*    disabled={isDownloading}*/}
                {/*    className="group disabled:opacity-60 hover:scale-110 transition-transform"*/}
                {/*>*/}
                {/*    {isDownloading ? (*/}
                {/*        <svg className="h-12 w-12 animate-spin text-gray-700" viewBox="0 0 24 24" fill="none">*/}
                {/*            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"*/}
                {/*                    strokeWidth="4"/>*/}
                {/*            <path className="opacity-75" fill="currentColor"*/}
                {/*                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"/>*/}
                {/*        </svg>*/}
                {/*    ) : (*/}
                {/*        <img src={ExcelExportIcon} alt="Excel Export" className="h-12 w-12"/>*/}
                {/*    )}*/}
                {/*</button>*/}

                {/* Center: Total Sum */}
                <h1 className="absolute left-1/2 transform -translate-x-1/2 text-4xl font-bold text-gray-900">
                    {dynamicTotalSumme.toLocaleString('de-DE')}€
                </h1>

                {/* Right: Date Inputs */}
                <div className="flex items-end gap-2">
                    <div className="flex flex-col">
                        <label className="text-sm text-gray-500">Von</label>
                        <input
                            type="date"
                            value={from}
                            onChange={e => setFrom(e.target.value)}
                            className="border rounded px-2 py-1 text-sm"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-sm text-gray-500">Bis</label>
                        <input
                            type="date"
                            value={to}
                            onChange={e => setTo(e.target.value)}
                            className="border rounded px-2 py-1 text-sm"
                        />
                    </div>
                    <button
                        onClick={() => {
                            fetchAllEvents();
                            fetchStundenkontingente();
                            fetchNichtEventStundenkontingente();
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                        disabled={loading}
                    >
                        {loading ? 'Lade...' : 'Lade Events'}
                    </button>
                </div>
            </div>

            {loading && <div className="text-center text-gray-500 animate-pulse">Lade Events...</div>}
            {!loading && events.length === 0 && (
                <div className="text-center text-gray-500">Keine Events im gewählten Zeitraum gefunden.</div>
            )}

            {/* Card Grid */}
            <div className="flex-1 overflow-auto">
                <div className="grid gap-4 p-2 mx-auto grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {cardElements}
                </div>
            </div>
        </main>
    );
};

export default Leistungspakete;
